import React, { useState, useEffect, useRef } from 'react';
import { Student } from '../../../../utils/firebase';
import Card from '../../../../components/ui/Card';
import Button from '../../../../components/ui/Button';

interface HorseRacingLotteryProps {
  students: Student[];
}

interface HorseData {
  position: number;
  speed: number;
  baseSpeed: number;
  acceleration: number;
}

const HorseRacingLottery: React.FC<HorseRacingLotteryProps> = ({ students }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [winners, setWinners] = useState<Student[]>([]);
  const [horses, setHorses] = useState<{ [key: string]: HorseData }>({});
  const [showRanking, setShowRanking] = useState(false);
  const animationRef = useRef<number | undefined>(undefined);
  const startTimeRef = useRef<number>(0);
  const horsesRef = useRef<{ [key: string]: HorseData }>({});

  // 초기 위치 설정
  useEffect(() => {
    const initialHorses: { [key: string]: HorseData } = {};
    students.forEach((student) => {
      initialHorses[student.uid] = {
        position: 0,
        speed: 0,
        baseSpeed: 0.5 + Math.random() * 0.5, // 각 말마다 다른 기본 속도
        acceleration: 0.3 + Math.random() * 0.4 // 각 말마다 다른 가속도
      };
    });
    setHorses(initialHorses);
    horsesRef.current = initialHorses;
  }, [students]);

  // 애니메이션 시작
  const startAnimation = () => {
    if (students.length === 0) return;
    
    setIsRunning(true);
    setShowRanking(false);
    setWinners([]);
    
    // 각 말의 초기 속성 설정 (매번 다르게)
    const initialHorses: { [key: string]: HorseData } = {};
    students.forEach((student) => {
      initialHorses[student.uid] = {
        position: 0,
        speed: 0,
        baseSpeed: 0.4 + Math.random() * 0.6, // 0.4 ~ 1.0
        acceleration: 0.2 + Math.random() * 0.5 // 0.2 ~ 0.7
      };
    });
    setHorses(initialHorses);
    horsesRef.current = initialHorses;
    startTimeRef.current = Date.now();

    const duration = 4000; // 4초 동안 애니메이션
    const trackWidth = 95; // 트랙 너비 (%)

    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      
      if (progress < 1) {
        const newHorses: { [key: string]: HorseData } = {};
        
        students.forEach(student => {
          const horse = horsesRef.current[student.uid];
          if (!horse) return;
          
          // 가속도 적용 (시간이 지날수록 속도 증가)
          const currentSpeed = horse.speed + horse.acceleration * 0.02;
          const maxSpeed = horse.baseSpeed * (1 + progress * 0.5); // 진행률에 따라 최대 속도 증가
          const finalSpeed = Math.min(currentSpeed, maxSpeed);
          
          // 위치 업데이트
          const newPosition = Math.min(
            horse.position + finalSpeed * 2,
            trackWidth
          );
          
          newHorses[student.uid] = {
            position: newPosition,
            speed: finalSpeed,
            baseSpeed: horse.baseSpeed,
            acceleration: horse.acceleration
          };
        });
        
        horsesRef.current = newHorses;
        setHorses({ ...newHorses });
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // 애니메이션 종료 - 최종 순위 결정
        finishRace();
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  // 경주 종료 및 순위 결정
  const finishRace = () => {
    setIsRunning(false);
    
    // 최종 위치에 따라 순위 결정
    const sortedStudents = [...students].sort((a, b) => {
      const posA = horsesRef.current[a.uid]?.position || 0;
      const posB = horsesRef.current[b.uid]?.position || 0;
      return posB - posA; // 위치가 높을수록 (오른쪽일수록) 우승
    });

    setWinners(sortedStudents);
    setShowRanking(true);
    
    // 최종 위치를 100%로 설정 (결승선까지)
    const finalHorses: { [key: string]: HorseData } = {};
    sortedStudents.forEach((student) => {
      const horse = horsesRef.current[student.uid];
      finalHorses[student.uid] = {
        ...horse,
        position: 95 // 결승선 위치
      };
    });
    setHorses(finalHorses);
  };

  // 컴포넌트 언마운트 시 애니메이션 정리
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // 말 아이콘 생성 (달리는 말 모양)
  const getHorseIcon = (student: Student, isRunning: boolean) => {
    const initial = student.name?.[0] || '?';
    const hue = (student.uid.charCodeAt(0) * 137.508) % 360;
    
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '50px',
          height: '50px',
          transform: isRunning ? 'translateY(0px)' : 'translateY(0px)',
          animation: isRunning ? 'gallop 0.5s infinite' : 'none',
          transition: 'transform 0.1s ease-out'
        }}
      >
        <style>
          {`
            @keyframes gallop {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              25% { transform: translateY(-3px) rotate(-2deg); }
              50% { transform: translateY(0px) rotate(0deg); }
              75% { transform: translateY(-3px) rotate(2deg); }
            }
          `}
        </style>
        {/* 말 몸체 */}
        <div
          style={{
            width: '45px',
            height: '35px',
            backgroundColor: `hsl(${hue}, 70%, 50%)`,
            borderRadius: '20px 8px 20px 8px',
            position: 'relative',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.1rem'
          }}
        >
          {initial}
          {/* 말 머리 */}
          <div
            style={{
              position: 'absolute',
              left: '-8px',
              top: '8px',
              width: '20px',
              height: '20px',
              backgroundColor: `hsl(${hue}, 70%, 45%)`,
              borderRadius: '50% 30% 50% 30%',
              border: '2px solid rgba(255,255,255,0.3)'
            }}
          />
          {/* 말 다리 (달리는 효과) */}
          {isRunning && (
            <>
              <div
                style={{
                  position: 'absolute',
                  bottom: '-8px',
                  left: '8px',
                  width: '4px',
                  height: '12px',
                  backgroundColor: `hsl(${hue}, 60%, 40%)`,
                  borderRadius: '2px',
                  animation: 'legMove 0.3s infinite alternate'
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '-8px',
                  right: '8px',
                  width: '4px',
                  height: '12px',
                  backgroundColor: `hsl(${hue}, 60%, 40%)`,
                  borderRadius: '2px',
                  animation: 'legMove 0.3s infinite alternate-reverse'
                }}
              />
            </>
          )}
        </div>
        <style>
          {`
            @keyframes legMove {
              0% { height: 12px; }
              100% { height: 8px; }
            }
          `}
        </style>
      </div>
    );
  };

  return (
    <Card style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', color: '#161616' }}>
          🏇 경마 방식 추첨
        </h2>
        <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '1.5rem' }}>
          학생들이 경주마처럼 달려서 순위를 결정합니다. 가장 먼저 결승선을 통과한 학생이 당첨됩니다!
        </p>
        <Button
          type="button"
          onClick={startAnimation}
          disabled={isRunning || students.length === 0}
          variant="primary"
          padding="16px 32px"
          style={{
            fontSize: '1rem',
            fontWeight: 600,
            boxShadow: isRunning ? 'none' : '0 4px 12px rgba(74, 144, 226, 0.3)',
            transition: 'all 0.2s'
          }}
        >
          {isRunning ? '🏃 경주 진행 중...' : '🚩 경주 시작!'}
        </Button>
      </div>

      {/* 경주 트랙 */}
      <div style={{ 
        position: 'relative',
        width: '100%',
        height: `${students.length * 80 + 40}px`,
        backgroundColor: '#E8F5E9',
        backgroundImage: `
          repeating-linear-gradient(
            90deg,
            transparent,
            transparent 49px,
            rgba(76, 175, 80, 0.1) 49px,
            rgba(76, 175, 80, 0.1) 50px
          )
        `,
        borderRadius: '0.5rem',
        padding: '1rem',
        marginBottom: '1.5rem',
        border: '3px solid #4CAF50',
        overflow: 'hidden',
        boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.1)'
      }}>
        {/* 트랙 레인 구분선 */}
        {students.map((_, index) => (
          <div
            key={`lane-${index}`}
            style={{
              position: 'absolute',
              left: '1rem',
              right: '1rem',
              top: `${index * 80 + 60}px`,
              height: '1px',
              backgroundColor: 'rgba(76, 175, 80, 0.3)',
              borderTop: '1px dashed rgba(76, 175, 80, 0.2)'
            }}
          />
        ))}
        
        {/* 출발선 */}
        <div
          style={{
            position: 'absolute',
            left: '1rem',
            top: '0',
            bottom: '0',
            width: '3px',
            backgroundColor: '#2196F3',
            borderRight: '3px dashed #2196F3'
          }}
        />
        
        {/* 말들 */}
        {students.map((student, index) => {
          const horse = horses[student.uid];
          const position = horse?.position || 0;
          
          return (
            <div
              key={student.uid}
              style={{
                position: 'absolute',
                left: `calc(1rem + ${position}%)`,
                top: `${index * 80 + 15}px`,
                transition: isRunning ? 'none' : 'left 0.3s ease-out',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                zIndex: 10,
                transform: isRunning ? 'translateY(0)' : 'translateY(0)'
              }}
            >
              {getHorseIcon(student, isRunning)}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px'
              }}>
                <span style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: 600,
                  color: '#161616',
                  whiteSpace: 'nowrap',
                  textShadow: '0 1px 2px rgba(255,255,255,0.8)'
                }}>
                  {student.name}
                </span>
                {isRunning && (
                  <span style={{
                    fontSize: '0.7rem',
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    {Math.round(position)}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
        
        {/* 결승선 */}
        <div
          style={{
            position: 'absolute',
            right: '1rem',
            top: '0',
            bottom: '0',
            width: '4px',
            backgroundColor: '#FF3838',
            borderLeft: '4px dashed #FF3838',
            boxShadow: '0 0 10px rgba(255, 56, 56, 0.5)',
            zIndex: 5
          }}
        />
        <div
          style={{
            position: 'absolute',
            right: '1rem',
            top: '10px',
            padding: '4px 8px',
            backgroundColor: '#FF3838',
            color: '#fff',
            fontSize: '0.75rem',
            fontWeight: 700,
            borderRadius: '4px',
            zIndex: 6
          }}
        >
          결승선
        </div>
      </div>

      {/* 순위 표시 */}
      {showRanking && winners.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#161616' }}>
            추첨 결과
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {winners.map((student, index) => (
              <div
                key={student.uid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '0.75rem',
                  backgroundColor: index === 0 ? '#FFF4E6' : index === 1 ? '#F0F0F0' : index === 2 ? '#FFF8E1' : '#F8F8F8',
                  borderRadius: '0.375rem',
                  border: index === 0 ? '2px solid #FFA726' : '1px solid #E0E0E0'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: index === 0 ? '#FFA726' : index === 1 ? '#9E9E9E' : index === 2 ? '#FFC107' : '#E0E0E0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  {index + 1}
                </div>
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#161616' }}>
                  {student.name}
                </span>
                {index === 0 && (
                  <span style={{ 
                    fontSize: '0.75rem', 
                    color: '#FFA726', 
                    fontWeight: 600,
                    marginLeft: 'auto'
                  }}>
                    🏆 당첨!
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default HorseRacingLottery;

